import { ODOO_BASE_URL } from '../../odooConfig';

const isLocalDevelopment =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Local requests go through setupProxy; deployed builds call the configured Odoo host.
const apiOrigin = isLocalDevelopment ? '' : ODOO_BASE_URL;
const apiBase = `${apiOrigin}/api/student/mobile`;

async function callOdoo(path, params = {}, token) {
  let response;
  try {
    response = await fetch(`${apiBase}/${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ jsonrpc: '2.0', method: 'call', params }),
    });
  } catch {
    throw new Error('Cannot reach the student login service. Please try again.');
  }

  if (!response.ok) {
    throw new Error('Cannot reach the student login service. Please try again.');
  }

  const contentType = response.headers?.get?.('content-type') || '';
  if (contentType && !contentType.toLowerCase().includes('application/json')) {
    throw new Error('The student login service returned an invalid response. Please try again later.');
  }

  let envelope;
  try {
    envelope = await response.json();
  } catch {
    throw new Error('The student login service returned an invalid response. Please try again later.');
  }
  if (envelope.error) {
    throw new Error('The student login service is unavailable.');
  }
  return envelope.result;
}

export async function loginStudent(studentId, password) {
  const result = await callOdoo('login', { student_no: studentId, password });
  if (!result?.success || !result.token || !result.student?.student_no) {
    if (['Authentication Failed', 'Forbidden', 'No Student Record'].includes(result?.error)) {
      throw new Error('Wrong Student ID or password.');
    }
    throw new Error(result?.message || 'The student login service is unavailable.');
  }
  return result;
}

export async function getStudentProfile(token) {
  const result = await callOdoo('profile', {}, token);
  if (!result?.success || !result.data?.academic_info?.student_no) {
    throw new Error('Your session has expired. Please sign in again.');
  }
  return result.data;
}

export async function getStudentTimetable(token) {
  const result = await callOdoo('timetable', {}, token);
  if (!result?.success) {
    if (result?.error === 'Unauthorized') {
      throw new Error('Your session has expired. Please sign in again.');
    }
    throw new Error(result?.message || 'Cannot load your timetable. Please try again.');
  }
  return result.data;
}

export async function getStudentFees(token, academicYear, status = 'all') {
  const result = await callOdoo('fees', {
    academic_year: academicYear || undefined,
    status,
  }, token);
  if (!result?.success) {
    if (result?.error === 'Unauthorized') {
      throw new Error('Your session has expired. Please sign in again.');
    }
    throw new Error(result?.message || 'Cannot load your fees. Please try again.');
  }
  return result.data;
}

export async function getStudentAttendance(token, subjectId) {
  const result = await callOdoo('attendance-summary', {
    subject_id: subjectId || undefined,
  }, token);
  if (!result?.success) {
    if (result?.error === 'Unauthorized') {
      throw new Error('Your session has expired. Please sign in again.');
    }
    throw new Error(result?.message || 'Cannot load your attendance. Please try again.');
  }
  return result.data;
}

export async function getStudentExamReports(token) {
  const result = await callOdoo('exam-reports', {}, token);
  if (!result?.success) {
    if (result?.error === 'Unauthorized') {
      throw new Error('Your session has expired. Please sign in again.');
    }
    throw new Error(result?.message || 'Cannot load your exam reports. Please try again.');
  }
  return result.data;
}

export async function changeStudentPassword(token, currentPassword, newPassword) {
  const result = await callOdoo('change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  }, token);
  if (!result?.success) {
    if (result?.error === 'Unauthorized') {
      throw new Error('Your session has expired. Please sign in again.');
    }
    if (result?.error === 'Authentication Failed') {
      throw new Error('Current password is incorrect.');
    }
    throw new Error(result?.message || 'Cannot change your password. Please try again.');
  }
  return result;
}

export async function logoutStudent(token) {
  if (token) await callOdoo('logout', {}, token);
}
